import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { subscriptionAPI } from '../services/api';
import { usePlanFeature, PlanFeatureKey } from '../hooks/usePlanFeature';
import { PLAN_FEATURE_GATES } from '../constants/planFeatureGates';
import { isHigherTier } from '../utils/subscriptionTiers';
import type { PublicPlanDefinition, SubscriptionTier } from '../types/platformOrganization';
import { StripeCheckoutLink } from './StripeCheckoutLink';
import { Card, LoadingSpinner } from './ui';

type PlanFeatureUpgradePromptProps = {
  feature: PlanFeatureKey;
  variant?: 'full' | 'compact';
  backLink?: { to: string; label: string };
};

function checkoutHref(
  summary: { checkoutUrls?: Partial<Record<SubscriptionTier, string | null>> } | undefined,
  tier: SubscriptionTier,
  fallback?: string | null
) {
  return summary?.checkoutUrls?.[tier] || fallback || null;
}

const PlanFeatureUpgradePrompt: React.FC<PlanFeatureUpgradePromptProps> = ({
  feature,
  variant = 'full',
  backLink
}) => {
  const meta = PLAN_FEATURE_GATES[feature];
  const { summary, isLoading } = usePlanFeature(feature);
  const { data: plansRes, isLoading: loadingPlans } = useQuery(['subscription-plans-public'], () =>
    subscriptionAPI.listPlans()
  );

  const plans = (plansRes?.data?.data || []) as PublicPlanDefinition[];
  const currentTier = summary?.tier || 'BASIC';
  const upgradePlans = plans.filter((p) => p.features[feature] && isHigherTier(p.tier, currentTier));

  if (isLoading || loadingPlans) {
    return variant === 'compact' ? (
      <LoadingSpinner size="sm" />
    ) : (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const primaryPlan =
    upgradePlans.find((p) => p.tier === meta.requiredTier) || upgradePlans[0];
  const primaryHref = primaryPlan
    ? checkoutHref(summary, primaryPlan.tier, primaryPlan.stripeCheckoutUrl)
    : null;

  if (variant === 'compact') {
    return (
      <div className="flex flex-col items-stretch sm:items-end gap-2">
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-right">
          Disponible desde el plan <strong>{meta.requiredPlanLabel}</strong>
        </p>
        {primaryHref ? (
          <StripeCheckoutLink href={primaryHref} className="w-full sm:w-auto justify-center">
            Actualizar plan
          </StripeCheckoutLink>
        ) : (
          <p className="text-xs text-slate-500 text-right">Contacte a soporte para actualizar su plan.</p>
        )}
      </div>
    );
  }

  return (
    <Card className="p-8 text-center max-w-xl mx-auto border-amber-200/80 bg-amber-50/30">
      <LockClosedIcon className="mx-auto h-12 w-12 text-amber-600" aria-hidden />
      <h2 className="mt-4 text-xl font-bold text-gray-900">{meta.title}</h2>
      <p className="mt-2 text-sm text-gray-600">{meta.description}</p>
      <p className="mt-4 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
        Su plan actual <strong>{summary?.plan?.label || 'Básico'}</strong> no incluye esta función.
        Actualice a <strong>{meta.requiredPlanLabel}</strong> o superior para habilitarla.
      </p>

      {upgradePlans.length > 0 ? (
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-center">
          {upgradePlans.map((p) => {
            const href = checkoutHref(summary, p.tier, p.stripeCheckoutUrl);
            if (!href) return null;
            return (
              <StripeCheckoutLink
                key={p.tier}
                href={href}
                variant={p.tier === meta.requiredTier ? 'primary' : 'outline'}
              >
                {p.label} — ${p.priceUsd}/mes
              </StripeCheckoutLink>
            );
          })}
        </div>
      ) : primaryHref ? (
        <div className="mt-6">
          <StripeCheckoutLink href={primaryHref}>Actualizar plan</StripeCheckoutLink>
        </div>
      ) : (
        <p className="mt-6 text-sm text-slate-500">Contacte a soporte para actualizar su plan.</p>
      )}

      <p className="mt-4 text-xs text-slate-500">
        Pago seguro en Stripe. Al completar el pago, el plan se activa automáticamente.
      </p>

      {backLink ? (
        <Link
          to={backLink.to}
          className="mt-6 inline-block text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          ← {backLink.label}
        </Link>
      ) : null}
    </Card>
  );
};

export default PlanFeatureUpgradePrompt;
